import { HTMLAttributes, PropsWithChildren, ReactNode } from 'react';
import {
  FieldValues,
  Path,
  RegisterOptions,
  UseFormReturn,
  useController
} from 'react-hook-form';
import FormField from '../../chat/FormField';
import Input from '../../chat/Input';
import get from 'lodash/get';

export type EmailProps<T extends FieldValues> = PropsWithChildren<
  {
    displayName?: string;
    errorDisplayName?: string;
    subLabel?: Parameters<typeof FormField>[0]['subLabel'];
    name?: Path<T>;
    form: UseFormReturn<T>;
    customValidation?: RegisterOptions<T>;
    required: boolean;
    showRequiredLabel?: Parameters<typeof FormField>[0]['showRequiredLabel'];
    layout?: Parameters<typeof FormField>[0]['layout'];
    placeholder?: string;
    suffixLabel?: Parameters<typeof FormField>[0]['suffixLabel'];
    disabled?: boolean;
    displayLarge?: Parameters<typeof FormField>[0]['displayLarge'];
    showClear?: boolean;
  } & HTMLAttributes<HTMLDivElement>
>;
const EmailInput = <T extends FieldValues>({
  children,
  name = 'email' as Path<T>,
  displayName = 'メールアドレス',
  errorDisplayName,
  form,
  customValidation,
  required,
  showRequiredLabel,
  subLabel,
  layout,
  placeholder,
  suffixLabel,
  disabled,
  displayLarge,
  showClear = true,
  ...props
}: EmailProps<T>) => {
  const {
    control,
    formState: { errors }
  } = form;
  const errorMsg = get(errors, name)?.message;

  const {
    field,
    fieldState: { invalid, isTouched, isDirty },
    formState: { touchedFields, dirtyFields }
  } = useController({
    name: name,
    control,
    rules: {
      required: {
        value: required,
        message: `${errorDisplayName || displayName}を入力してください。`
      },
      maxLength: {
        value: 80,
        message: `${
          errorDisplayName || displayName
        }の入力文字は80文字以内にしてください。`
      },
      pattern: {
        value: /^\w+([.-]?\w+)*.?@\w+([.-]?\w+)*(\.\w{2,10})+$/,
        message: `メールアドレスの入力形式を確認してください。`
      },
      ...(customValidation || {})
    }
  });

  return (
    <FormField
      label={displayName}
      subLabel={subLabel}
      field={Input}
      fieldProps={{
        type: 'email',
        placeholder: placeholder,
        disabled: disabled,
        showClear: showClear
      }}
      value={field.value}
      onChange={field.onChange}
      rhfRef={field.ref}
      error={!!errorMsg}
      errorText={errorMsg?.toString()}
      layout={layout}
      showRequiredLabel={showRequiredLabel}
      suffixLabel={suffixLabel}
      displayLarge={displayLarge}
    />
  );
};

export default EmailInput;
